import { debounce } from "lodash";
import { Map, MapEvent, View } from "ol";
import { Coordinate } from "ol/coordinate";
import { toLonLat, fromLonLat } from "ol/proj";
import {
  createContext,
  useCallback,
  useMemo,
  useState,
  useRef,
  WheelEvent,
  useEffect,
  ReactNode,
} from "react";
import { Location } from "../MapContainer";

/**
 * 다중 지도 동기화 컨테이너 props — 자식 `SyncMap` 들의 center/zoom/rotation 을 공유.
 *
 * 개별 자식이 `isDecoupled=true` 면 그 맵은 독립 제어. side-by-side 비교 UI 에 자주 쓴다.
 */
export interface SyncMapGroupProps {
  /**
   * @default true
   */
  sync?: boolean;
  /**
   * @default [127.9745613, 37.3236563]
   */
  center?: Location;

  /**
   * @default 15
   */
  zoomLevel?: number;

  /**
   * @default 0
   */
  rotate?: number;

  children?: ReactNode;
}

export interface SyncMapContextProps {
  sharedView: View;
  controlledCenter: Location;
  controlledZoomLevel: number;
  controlledRotation: number;
  adjustCenter: (location: Location) => void;
  adjustZoomLevel: (level: number) => void;
  onWheelHandler: (event: WheelEvent<HTMLDivElement>, map: Map) => void;
  onZoomHandler: (event: MapEvent, map: Map) => void;
  adjustRotate: (rotation: number) => void;
  registerMap: (map: Map, isDecoupled: boolean) => void;
  unregisterMap: (map: Map) => void;
}

export const SyncMapContext = createContext<SyncMapContextProps | null>(null);

export const SyncMapGroup = ({
  center = [127.9745613, 37.3236563],
  zoomLevel = 15,
  children,
  rotate = 0,
}: SyncMapGroupProps) => {
  const [controlledCenter, setControlledCenter] = useState(center);
  const [controlledZoomLevel, setControlledZoomLevel] = useState(zoomLevel);
  const [controlledRotation, setControlledRotation] = useState(rotate);

  // 최신 상태를 ref로 유지하여 콜백 의존성 제거
  const controlledCenterRef = useRef(controlledCenter);
  controlledCenterRef.current = controlledCenter;

  const controlledZoomLevelRef = useRef(controlledZoomLevel);
  controlledZoomLevelRef.current = controlledZoomLevel;

  const controlledRotationRef = useRef(controlledRotation);
  controlledRotationRef.current = controlledRotation;

  // 공유 View 생성
  const sharedView = useRef(
    new View({
      center: fromLonLat(center),
      zoom: zoomLevel,
      rotation: (rotate * Math.PI) / 180,
    })
  );

  // 동기화된 맵들을 추적
  const syncedMaps = useRef<Set<Map>>(new Set());
  const decoupledMaps = useRef<Set<Map>>(new Set());

  // 외부에서 설정된 값들을 추적 (props 변경 시에만 업데이트)
  const externalCenter = useRef(center);
  const externalZoomLevel = useRef(zoomLevel);
  const externalRotation = useRef(rotate);

  const handleWheel = useRef(
    debounce((event, map: Map) => {
      // decoupled 맵이 아닌 경우에만 동기화
      if (!decoupledMaps.current.has(map)) {
        const newZoom = map.getView().getZoom() as number;
        const newCenter = toLonLat(
          map.getView().getCenter() as Coordinate
        ) as Location;

        // 상태 업데이트 (UI 동기화용)
        setControlledZoomLevel(newZoom);
        setControlledCenter(newCenter);

        // 다른 동기화된 맵들에 변경사항 적용
        syncedMaps.current.forEach((syncedMap) => {
          if (syncedMap !== map) {
            syncedMap.getView().setZoom(newZoom);
            syncedMap.getView().setCenter(fromLonLat(newCenter));
          }
        });
      }
    }, 300)
  );

  const onWheelHandler = useCallback(
    (event: WheelEvent<HTMLDivElement>, map: Map) => {
      event.persist();
      handleWheel.current(event, map);
    },
    []
  );

  const onZoomHandler = useCallback((event: MapEvent, map: Map) => {
    handleWheel.current(event, map);
  }, []);

  // ref 패턴으로 controlledCenter 의존성 제거 → 콜백 안정화
  const adjustCenter = useCallback((location: Location) => {
    const current = controlledCenterRef.current;
    if (location[0] !== current[0] || location[1] !== current[1]) {
      setControlledCenter(location);
      const centerCoord = fromLonLat(location);

      sharedView.current.setCenter(centerCoord);

      syncedMaps.current.forEach((map) => {
        map.getView().setCenter(centerCoord);
      });
    }
  }, []);

  const adjustZoomLevel = useCallback((level: number) => {
    if (level !== controlledZoomLevelRef.current) {
      setControlledZoomLevel(level);

      sharedView.current.setZoom(level);

      syncedMaps.current.forEach((map) => {
        map.getView().setZoom(level);
      });
    }
  }, []);

  const adjustRotate = useCallback((rotation: number) => {
    if (rotation !== controlledRotationRef.current) {
      setControlledRotation(rotation);
      const rotationRad = (rotation * Math.PI) / 180;

      sharedView.current.setRotation(rotationRad);

      syncedMaps.current.forEach((map) => {
        map.getView().setRotation(rotationRad);
      });
    }
  }, []);

  const registerMap = useCallback((map: Map, isDecoupled: boolean) => {
    if (isDecoupled) {
      decoupledMaps.current.add(map);
    } else {
      syncedMaps.current.add(map);
      // 동기화된 맵의 View를 공유 View로 설정
      map.setView(sharedView.current);
    }
  }, []);

  const unregisterMap = useCallback((map: Map) => {
    syncedMaps.current.delete(map);
    decoupledMaps.current.delete(map);
  }, []);

  // props 변경 시에만 업데이트 (직접 비교로 JSON.stringify 제거)
  useEffect(() => {
    if (rotate !== externalRotation.current) {
      externalRotation.current = rotate;
      setControlledRotation(rotate);
      const rotationRad = (rotate * Math.PI) / 180;
      sharedView.current.setRotation(rotationRad);

      syncedMaps.current.forEach((map) => {
        map.getView().setRotation(rotationRad);
      });
    }
  }, [rotate]);

  useEffect(() => {
    const [extLon, extLat] = externalCenter.current;
    if (center[0] !== extLon || center[1] !== extLat) {
      externalCenter.current = center;
      setControlledCenter(center);
      const centerCoord = fromLonLat(center);
      sharedView.current.setCenter(centerCoord);

      syncedMaps.current.forEach((map) => {
        map.getView().setCenter(centerCoord);
      });
    }
  }, [center]);

  useEffect(() => {
    if (zoomLevel !== externalZoomLevel.current) {
      externalZoomLevel.current = zoomLevel;
      setControlledZoomLevel(zoomLevel);
      sharedView.current.setZoom(zoomLevel);

      syncedMaps.current.forEach((map) => {
        map.getView().setZoom(zoomLevel);
      });
    }
  }, [zoomLevel]);

  const value = useMemo(
    () => ({
      sharedView: sharedView.current,
      controlledCenter,
      controlledZoomLevel,
      controlledRotation,
      adjustCenter,
      adjustZoomLevel,
      onWheelHandler,
      onZoomHandler,
      adjustRotate,
      registerMap,
      unregisterMap,
    }),
    [
      controlledRotation,
      controlledCenter,
      controlledZoomLevel,
      adjustCenter,
      adjustZoomLevel,
      onWheelHandler,
      onZoomHandler,
      adjustRotate,
      registerMap,
      unregisterMap,
    ]
  );

  return (
    <SyncMapContext.Provider value={value}>{children}</SyncMapContext.Provider>
  );
};
